import { Injectable, ElementRef } from '@angular/core';

export interface ITruncatedList<T extends { Name: string }> {
    visibleItems: T[];
    hiddenItems: T[];
    hiddenCount: number;
}

@Injectable()
export class TextTruncationService {
    private readonly SEPARATOR = ', ';
    private readonly MORE_TEXT_PADDING = 8; // Padding for "+n more" text

    calculateTruncatedList<T extends { Name: string }>(
        container: ElementRef,
        items: T[],
        labelWidth: number,
        suffix: string,
    ): ITruncatedList<T> {
        if (!container || !items.length) {
            return { visibleItems: [], hiddenItems: [], hiddenCount: 0 };
        }

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
            return { visibleItems: items, hiddenItems: [], hiddenCount: 0 };
        }

        // Get computed style of the container
        const computedStyle = window.getComputedStyle(container.nativeElement);
        context.font = `${computedStyle.fontStyle} ${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
        context.letterSpacing = computedStyle.letterSpacing || '0.4px';
        const containerWidth = container.nativeElement.offsetWidth;

        // Calculate max width for "+99 {suffix}" with extra padding for safety
        const maxMoreText = `+99 ${suffix}`; // Use 99 to ensure enough space for any number
        const maxMoreWidth = Math.round(context.measureText(maxMoreText).width + this.MORE_TEXT_PADDING * 2);

        // Available width for items, accounting for label and max "+n more" text
        const availableWidth = Math.round(containerWidth - labelWidth - maxMoreWidth);

        if (availableWidth <= 0) {
            return {
                visibleItems: [],
                hiddenItems: items,
                hiddenCount: items.length,
            };
        }

        let currentWidth = 0;
        const visibleItems: T[] = [];

        for (let i = 0; i < items.length; i++) {
            const itemText = items[i].Name;
            const itemWidth = Math.round(context.measureText(itemText + this.SEPARATOR).width);
            // Check if this item would fit in the remaining space
            if (currentWidth + itemWidth <= availableWidth) {
                visibleItems.push(items[i]);
                currentWidth += itemWidth;
            } else {
                // Check if only one item would be hidden and if it can fit without the "+1" indicator
                const remainingItems = items.slice(i);
                if (remainingItems.length === 1) {
                    const lastItemWidth = Math.round(
                        context.measureText(remainingItems[0].Name + this.SEPARATOR).width,
                    );
                    // Try to fit the last item without the "+1" indicator space
                    if (currentWidth + lastItemWidth <= containerWidth - labelWidth) {
                        return {
                            visibleItems: items,
                            hiddenItems: [],
                            hiddenCount: 0,
                        };
                    }
                }

                // If we can't fit more items or it's not a special case, return what we have
                return {
                    visibleItems,
                    hiddenItems: remainingItems,
                    hiddenCount: items.length - visibleItems.length,
                };
            }
        }

        return {
            visibleItems,
            hiddenItems: [],
            hiddenCount: 0,
        };
    }
}
